import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { useAuth } from '@/lib/auth/AuthProvider';
import { AppShell } from '@/components/layout/AppShell';
import { LoginPage } from '@/pages/Login';

// Lazy load pages for better initial load performance
const Dashboard = lazy(() => import('@/pages/Dashboard').then(m => ({ default: m.Dashboard })));
const Outputs = lazy(() => import('@/pages/Outputs').then(m => ({ default: m.Outputs })));
const History = lazy(() => import('@/pages/History').then(m => ({ default: m.History })));
const Instructions = lazy(() => import('@/pages/Instructions').then(m => ({ default: m.Instructions })));
const Keywords = lazy(() => import('@/pages/Keywords').then(m => ({ default: m.Keywords })));
const Projects = lazy(() => import('@/pages/Projects').then(m => ({ default: m.Projects })));
const Schedule = lazy(() => import('@/pages/Schedule').then(m => ({ default: m.Schedule })));
const Preferences = lazy(() => import('@/pages/Preferences').then(m => ({ default: m.Preferences })));
const People = lazy(() => import('@/pages/People').then(m => ({ default: m.People })));
const Conversations = lazy(() => import('@/pages/Conversations').then(m => ({ default: m.Conversations })));
const Locations = lazy(() => import('@/pages/Locations').then(m => ({ default: m.Locations })));
const Settings = lazy(() => import('@/pages/Settings').then(m => ({ default: m.Settings })));
const Admin = lazy(() => import('@/pages/Admin').then(m => ({ default: m.Admin })));
const Billing = lazy(() => import('@/pages/Billing').then(m => ({ default: m.Billing })));
const BillingAdmin = lazy(() => import('@/pages/BillingAdmin').then(m => ({ default: m.BillingAdmin })));
const FeatureFlags = lazy(() => import('@/pages/FeatureFlags').then(m => ({ default: m.FeatureFlags })));
const Reviews = lazy(() => import('@/pages/Reviews').then(m => ({ default: m.Reviews })));
const Insights = lazy(() => import('@/pages/Insights').then(m => ({ default: m.Insights })));
const AnalyticsAdmin = lazy(() => import('@/pages/AnalyticsAdmin').then(m => ({ default: m.AnalyticsAdmin })));
const AnalyticsUser = lazy(() => import('@/pages/AnalyticsUser').then(m => ({ default: m.AnalyticsUser })));
const AgentDiagnostics = lazy(() => import('@/pages/AgentDiagnostics').then(m => ({ default: m.AgentDiagnostics })));
const TokenAnalytics = lazy(() => import('@/pages/TokenAnalytics').then(m => ({ default: m.TokenAnalytics })));
const Permissions = lazy(() => import('@/pages/Permissions').then(m => ({ default: m.Permissions })));
const UserAdmin = lazy(() => import('@/pages/UserAdmin').then(m => ({ default: m.UserAdmin })));
const DataSources = lazy(() => import('@/pages/DataSources').then(m => ({ default: m.DataSources })));
const Evidence = lazy(() => import('@/pages/Evidence').then(m => ({ default: m.Evidence })));

function PageLoader() {
  return (
    <div className="flex h-64 items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-dark-950">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
          <p className="text-sm text-dark-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const { isSuperAdmin } = useAuth();

  if (!isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
      />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AppShell>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/keywords" element={<Keywords />} />
                  <Route path="/projects" element={<Projects />} />
                  <Route path="/instructions" element={<Instructions />} />
                  <Route path="/schedule" element={<Schedule />} />
                  <Route path="/preferences" element={<Preferences />} />
                  <Route path="/people" element={<People />} />
                  <Route path="/conversations" element={<Conversations />} />
                  <Route path="/locations" element={<Locations />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/outputs" element={<Outputs />} />
                  <Route path="/history" element={<History />} />
                  <Route path="/admin" element={<SuperAdminRoute><Admin /></SuperAdminRoute>} />
                  <Route path="/billing" element={<Billing />} />
                  <Route path="/billing/admin" element={<SuperAdminRoute><BillingAdmin /></SuperAdminRoute>} />
                  <Route path="/feature-flags" element={<SuperAdminRoute><FeatureFlags /></SuperAdminRoute>} />
                  <Route path="/analytics" element={<AnalyticsUser />} />
                  <Route path="/analytics/admin" element={<SuperAdminRoute><AnalyticsAdmin /></SuperAdminRoute>} />
                  <Route path="/agent-diagnostics" element={<SuperAdminRoute><AgentDiagnostics /></SuperAdminRoute>} />
                  <Route path="/analytics/tokens" element={<SuperAdminRoute><TokenAnalytics /></SuperAdminRoute>} />
                  <Route path="/permissions" element={<Permissions />} />
                  <Route path="/admin/users" element={<SuperAdminRoute><UserAdmin /></SuperAdminRoute>} />
                  <Route path="/data-sources" element={<DataSources />} />
                  <Route path="/evidence" element={<Evidence />} />
                  <Route path="/reviews" element={<Reviews />} />
                  <Route path="/insights" element={<Insights />} />
                  {/* Redirect old routes */}
                  <Route path="/configuration" element={<Navigate to="/keywords" replace />} />
                </Routes>
              </Suspense>
            </AppShell>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

